import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import CremeLogo from './CremeLogo';
import { useScroll, useScrollProgress } from '../context/ScrollContext';
import { MobileMenu, DesktopDropdown, MENU_DATA, SearchIcon, AccountIcon } from './MegaMenu';
import { INSTAGRAM_URL } from '../config/site';

const LOGO_HEIGHT_MIN = 94;
const LOGO_HEIGHT_MAX = 110;

function HamburgerIcon() {
  return (
    <span className="block w-5 h-4 flex flex-col justify-between" aria-hidden>
      <span className="block w-full h-0.5 bg-theme-font rounded-full" />
      <span className="block w-full h-0.5 bg-theme-font rounded-full" />
      <span className="block w-full h-0.5 bg-theme-font rounded-full" />
    </span>
  );
}

function Header() {
  const scrolled = useScroll();
  const scrollProgress = useScrollProgress();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const logoHeightPx = LOGO_HEIGHT_MAX - (LOGO_HEIGHT_MAX - LOGO_HEIGHT_MIN) * scrollProgress;

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-[999] bg-sage transition-all duration-300 h-[120px] md:h-auto ${
          'border-b-[0.5px] border-white'
        }`}
      >
        {/* Main bar */}
        <div className="px-6 md:px-10 lg:px-16 h-full md:h-[120px]">
          <div className="relative flex items-center h-full">
            {/* Left: hamburger (mobile) + search */}
            <div className="flex-none md:flex-1 flex items-center gap-2 min-w-0">
              <button
                type="button"
                onClick={() => setMenuOpen(true)}
                aria-label="Open menu"
                className="md:hidden w-10 h-10 flex items-center justify-center border-0 bg-transparent cursor-pointer p-0 hover:opacity-80 transition-opacity"
              >
                <HamburgerIcon />
              </button>
              <a
                href="#flavors"
                aria-label="Browse flavors"
                className="hidden md:flex w-10 h-10 items-center justify-center border-0 bg-transparent cursor-pointer p-0 hover:opacity-80 transition-opacity text-theme-font"
              >
                <SearchIcon />
              </a>
            </div>

            {/* Logo center: 1/4 smaller on mobile */}
            <Link
              to="/"
              style={
                {
                  '--logo-height': `${logoHeightPx}px`,
                } as React.CSSProperties
              }
              className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center text-theme-font no-underline h-[var(--logo-height)] [&>img]:h-[var(--logo-height)] [&>img]:w-auto [&>img]:transition-[height] [&>img]:duration-300 [&>img]:ease-out pointer-events-auto"
              aria-label="CRÈME – Home"
            >
              <CremeLogo />
            </Link>

            {/* Right: account icon */}
            <div className="flex-1 flex items-center justify-end min-w-0">
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Message us on Instagram"
                className="w-10 h-10 flex items-center justify-center border-0 bg-transparent cursor-pointer p-0 hover:opacity-80 transition-opacity text-theme-font"
              >
                <AccountIcon />
              </a>
            </div>
          </div>
        </div>

        {/* Desktop nav row */}
        <nav
          className={`hidden md:flex items-center justify-center gap-12 lg:gap-16 overflow-hidden transition-all duration-300 border-t border-theme-font/30 ${
            scrolled ? 'max-h-0 opacity-0 !border-transparent' : 'max-h-[50px] h-[50px] opacity-100'
          }`}
        >
          {MENU_DATA.map((cat) => (
            <DesktopDropdown
              key={cat.label}
              category={cat}
              isOpen={openDropdown === cat.label}
              onOpen={() => setOpenDropdown(cat.label)}
              onClose={() => setOpenDropdown(null)}
            />
          ))}
        </nav>
      </header>

      {/* Mobile menu */}
      <MobileMenu isOpen={menuOpen} onClose={closeMenu} />
    </>
  );
}

export default Header;
